import { Sortable } from '@cco/model';
import { DateTime } from 'luxon';

const nullCompareFn = <T>(compareFn: (a: T, b: T) => number, nullFirst = false) => (a: T, b: T) => {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null && b != null) {
    return nullFirst ? -1 : 1;
  }
  if (a != null && b == null) {
    return nullFirst ? 1 : -1;
  }

  return compareFn(a, b);
};

const nullFirstCompareFn = <T>(compareFn: (a: T, b: T) => number) => nullCompareFn(compareFn, true);
const nullLastCompareFn = <T>(compareFn: (a: T, b: T) => number) => nullCompareFn(compareFn, false);

const completeWordNumericRegex = /^[0-9]+(\.[0-9]+)?$/;
const longestNumericRegex = /^[0-9]+(\.[0-9]+)?/;
const longestNonNumericRegex = /^[^0-9]+/;

export const numericLocaleCompare = (a: string, b: string) => {
  if (a == null || b == null)
    return 0;

  if (completeWordNumericRegex.test(a) && completeWordNumericRegex.test(b))
    return +a - +b;

  let nonNumericSegmentA = '', nonNumericSegmentB = '';
  let numericSegmentA = '', numericSegmentB = '';
  let posA = 0, posB = 0;
  while (posA < a.length && posB < b.length) {
    if (completeWordNumericRegex.test(a[posA]) && completeWordNumericRegex.test(b[posB])) {
      numericSegmentA = longestNumericRegex.exec(a.slice(posA))[0];
      numericSegmentB = longestNumericRegex.exec(b.slice(posB))[0];
      posA += numericSegmentA.length;
      posB += numericSegmentB.length;

      const numericDiff = +numericSegmentA - +numericSegmentB;
      if (numericDiff !== 0)
        return numericDiff;
    } else if (!completeWordNumericRegex.test(a[posA]) && !completeWordNumericRegex.test(b[posB])) {
      nonNumericSegmentA = longestNonNumericRegex.exec(a.slice(posA))[0];
      nonNumericSegmentB = longestNonNumericRegex.exec(b.slice(posB))[0];
      posA += nonNumericSegmentA.length;
      posB += nonNumericSegmentB.length;

      const comp = nonNumericSegmentA.localeCompare(nonNumericSegmentB);
      if (comp !== 0)
        return comp;
    } else {
      // Use .localeCompare if one entry is numeric and the other is not
      const comp = a[posA].localeCompare(b[posB]);
      if (comp !== 0)
        return comp;

      posA++;
      posB++;
    }
  }

  return a.localeCompare(b);
};

export const numberComparer = <T>(mapFn: (obj: T) => number) =>
  nullLastCompareFn((a: T, b: T) => {
    const aOrderBy = mapFn(a);
    const bOrderBy = mapFn(b);

    return nullLastCompareFn((c: number, d: number) => {
      if (c === d) {
        return 0;
      }
      if (c < d) {
        return -1;
      }
      return 1;
    })(aOrderBy, bOrderBy);
  });

export const orderByComparer = numberComparer((a: Sortable) => a?.orderBy);

interface HasScheduledInterval {
  scheduledInterval: {
    start: string;
    end: string;
  };
}

export const instantCompareFn = <T>(mapFn: (T) => string) =>
  nullFirstCompareFn((a: T, b: T) => {
    const aInstantString = mapFn(a);
    const bInstantString = mapFn(b);

    return nullFirstCompareFn((c: string, d: string) => {
      const aStart = DateTime.fromISO(c);
      const bStart = DateTime.fromISO(d);

      if (!aStart.isValid && !bStart.isValid) {
        return 0;
      }
      if (aStart.isValid && !bStart.isValid) {
        return 1;
      }
      if (!aStart.isValid && bStart.isValid) {
        return -1;
      }

      if (aStart < bStart) {
        return -1;
      }
      if (aStart > bStart) {
        return 1;
      }

      return 0;
    })(aInstantString, bInstantString);
  });

export const scheduledIntervalComparer = (a: HasScheduledInterval, b: HasScheduledInterval) =>
  a?.scheduledInterval?.start == null ? -1 : b?.scheduledInterval?.start == null ? 1 :  b?.scheduledInterval?.start?.localeCompare(a?.scheduledInterval?.start);

export class SortableUtil {
  /**
   * Clone an array, then sort with the orderByComparer to avoid modifying the
   * order of the original array.
   */
  public static sortClone<T>(array: T[], compareFn: (a: T, b: T) => number): T[] {
    if (!array) {
      return null;
    }

    return array.map(x => x).sort(compareFn);
  }

  public static sortCloneByOrderBy<T extends Sortable>(array: T[]): T[] {
    return SortableUtil.sortClone<T>(array, orderByComparer);
  }

  public static sortCloneWithMapToSortable<T>(array: T[], mapFn: (T) => Sortable): T[] {
    return SortableUtil.sortClone(
      array,
      numberComparer(obj => mapFn(obj)?.orderBy),
    );
  }

  public static sortByTimestampProperty<T>(array: T[], mapFn: (T) => string): T[] {
    return SortableUtil.sortClone(array, instantCompareFn(mapFn));
  }

  public static sortCloneByMappedNumber<T>(array: T[], mapFn: (T) => number): T[] {
    return SortableUtil.sortClone(array, numberComparer(mapFn));
  }
}
